"use client";

import { useContext, useEffect } from "react";
import dynamic from "next/dynamic";
import { useResponsive } from "@/hooks/use-responsive";
import { useRender } from "@/hooks/use-render";
import { NavigationContext } from "@/context/Navigation";
import type { MarkdownContent } from "@/app/page";
import { HomeFeatureSection } from "@/components/landing/HomeFeatureSection";

// Dynamic imports for optimization
const DocumentReviews = dynamic(
  () => import("@/components/landing/DocumentReviews")
);
const ByLawsSection = dynamic(
  () => import("@/components/landing/ByLawsSection")
);
const SiteHeader = dynamic(() => import("@/components/SiteHeader"));
const SiteFooter = dynamic(() => import("@/components/SiteFooter"));
const HelpChangeSection = dynamic(
  () => import("@/components/HelpChangeSection"),
  { ssr: false }
);
const MoreFAQSection = dynamic(() => import("@/components/MoreFAQSection"), {
  ssr: false,
});
const Clients = dynamic(() => import("@/components/Clients"), { ssr: false });
const FeedbackSection = dynamic(() => import("@/components/FeedbackSection"), {
  ssr: false,
});
const HomeCalloutSection = dynamic(
  () => import("@/components/HomeCalloutSection")
);
const FeatureSection = dynamic(
  () => import("@/components/landing/FeatureSection")
);
const TestimonialsSection = dynamic(
  () => import("@/components/landing/GoogleReview")
);
const FAQSection = dynamic(() => import("@/components/FAQSection"), {
  ssr: false,
});

interface ClientHomeContentProps {
  rootMarkdownFiles: MarkdownContent[];
  contentTextMarkdownFiles: MarkdownContent[];
}

function ClientHomeContent({
  rootMarkdownFiles,
  contentTextMarkdownFiles,
}: ClientHomeContentProps) {
  const render = useRender({ smoothing: true });
  const { responsive } = useResponsive();
  const navigationContext = useContext(NavigationContext);

  // Find content for each section
  const documentReviewsContent = contentTextMarkdownFiles.find(
    (file) => file.slug === "document-reviews"
  );
  const homeCalloutContent = contentTextMarkdownFiles.find(
    (file) => file.slug === "home-callout"
  );
  const featureSectionContent = contentTextMarkdownFiles.find(
    (file) => file.slug === "feature-section"
  );
  const byLawsContent = contentTextMarkdownFiles.find(
    (file) => file.slug === "by-laws"
  );
  const homeFeatureSectionContent = contentTextMarkdownFiles.find(
    (file) => file.slug === "home-feature-section"
  );
  const faqContent = contentTextMarkdownFiles.find(
    (file) => file.slug === "faq"
  );
  const feedbackSectionContent = contentTextMarkdownFiles.find(
    (file) => file.slug === "feedback-section"
  );

  useEffect(() => {
    navigationContext.hideLoadingScreen();
  }, [navigationContext]);

  return render(
    <SiteHeader scheme="light" />,
    <main>
      <DocumentReviews markdownContent={documentReviewsContent} />
      <HomeCalloutSection markdownContent={homeCalloutContent} />
      <FeatureSection markdownContent={featureSectionContent} />
      <ByLawsSection
        markdownContent={byLawsContent}
        py={responsive({
          xs: 40,
          lg: 64,
        })}
      />
      <HomeFeatureSection
        sectionId="home-feature-section"
        markdownContent={homeFeatureSectionContent}
      />
      <Clients />
      <TestimonialsSection />
      <FAQSection markdownContent={faqContent} />
      <MoreFAQSection />
      <FeedbackSection markdownContent={feedbackSectionContent} />
      <HelpChangeSection />
      <SiteFooter />
    </main>
  );
}

export default ClientHomeContent;
